import { InjectionToken } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { TimeoutService } from '@df/ng/timeout.service';
import { IAddressData } from '@df/session/api/address-book/address';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import isArray from 'lodash-es/isArray';
import { Subject, Subscription } from 'rxjs';

export interface IHint {
    Title?: string;
    Description?: string;
}

export interface IAddressSearcherData {
    data: IAddressData;
    regions?: Nexus.Lib.General.Region[];
    //
    detectChanges: () => void;
}

export interface IAddressSearcherOptions {
    company: boolean;
    runOnQueryChange: boolean;
}

export const ADDRESS_SEARCHER_OPTIONS = new InjectionToken<IAddressSearcherOptions>('DF_ADDRESS_SEARCHER_OPTIONS', {
    providedIn: 'root',
    factory: () => DEFAULT_ADDRESS_SEARCHER_OPTIONS
});

export const DEFAULT_ADDRESS_SEARCHER_OPTIONS: IAddressSearcherOptions = {
    company: false,
    runOnQueryChange: true
};
export abstract class AddressSearcher {
    hintsChanged = new Subject<void>();
    selectionApplied = new Subject<void>();

    private subscriptions = new Subscription();

    /**
     * Flag if form should be visible (forced mode)
     * @type {boolean}
     */
    isManual = false;

    /**
     * Hints array (those can be addresses etc)
     * @type {IHint[]}
     */
    protected _hints?: IHint[];

    /**
     * Use setHints instead
     */
    private set hints(value: IHint[] | undefined) {
        this._hints = value;

        this.addressSearcherData.detectChanges();
    }
    get hints(): IHint[] | undefined {
        return this._hints;
    }

    /**
     * Query used to retrieve hints
     */
    private hintsQuery?: string;

    /**
     * Searched query string
     * @type {string}
     */
    protected _query?: string;

    /**
     * Searched query used to retrieve hints
     * @type {string}
     */
    protected _hintsQuery?: string;

    status = new Status();

    @NgInject(TimeoutService)
    timeoutService!: TimeoutService;

    @NgInject(RequestService)
    requestService!: RequestService;

    setHints(query: string, hints: IHint[]): void {
        this.hintsQuery = query;
        this.hints = hints;
        this.hintsChanged.next();
    }

    constructor(
        protected addressSearcherData: IAddressSearcherData,
        protected options: IAddressSearcherOptions
    ) {
        this.subscriptions.add(this.status.subscribe(() => this.addressSearcherData.detectChanges()));
    }

    onDestroy() {
        this.query = undefined;
        this.subscriptions.unsubscribe();
    }

    set query(value: string | undefined) {
        this._query = value;
        if (this.options?.runOnQueryChange) {
            this.search();
        }
    }

    get query(): string | undefined {
        return this._query;
    }

    /**
     * Public method to trigger search
     */
    search(...args: any[]) {
        // run query if available
        const query = this.getRequestQuery(args);
        if (query) {
            this.status.busy = true;
            // if selected a hint, call immediately
            if (this.isWithArgs(...args)) {
                this._search(...args);
            }
            // if typing then wait for 1 second
            else {
                this._searchDebounced(...args);
            }
        } else {
            this.status.busy = false;
            this.clear();
        }
    }

    /**
     * Internal method to trigger search
     */
    protected _search(...args: any[]) {
        const query = this.getRequestQuery(args);
        if (query) {
            this.runSearch(query, ...args);
        } else {
            this.clear();
        }
    }
    /**
     * Debounced search method for typing
     */
    protected _searchDebounced = this.timeoutService.debounce((...args: any[]) => this._search(...args), 1000);

    /**
     * We assume that with no params we use general search while with params we search by hints
     */
    protected getRequestQuery(args: any[]): string | undefined {
        if (!this.isAvailable) {
            return undefined;
        }
        const query = this.isWithArgs(...args) ? this.hintsQuery : this.query;
        if (!query || query.length < 3) {
            return undefined;
        }
        return query;
    }

    /**
     * Abstract method that should process query using status
     */
    protected abstract runSearch(query: string, ...args: any[]): void;

    /**
     * Flag if service is currently availble
     * @type {boolean}
     */
    get isAvailable(): boolean {
        return true;
    }

    clear(): void {
        this.hints = [];
        this.hintsChanged.next();
    }

    protected applySelection(...args: any[]) {
        this.isManual = true;
        this.clear();
        this.selectionApplied.next();
    }

    protected pushTo(target: string, source: string | string[], existing?: string[]): string {
        // call recurently if array
        if (isArray(source)) {
            source.forEach(item => {
                target = this.pushTo(target, item, existing);
            });
            return target;
        }
        if (!existing) {
            existing = [];
        }
        existing.push(target);
        // replace if string does not belong to any of sources
        if (
            source &&
            !find(existing, existingTarget => {
                return existingTarget.indexOf(source) !== -1;
            })
        ) {
            return compact([source, target]).join(', ');
        }
        return target;
    }

    private isWithArgs(...args: any[]): boolean {
        return !(!args.length || (args.length === 1 && args[0] === undefined));
    }

    abstract trackByHint(index: number, hint: IHint);
}
